import {MRT_SortingState} from 'material-react-table';
import React, {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Headline} from '@lightricks/react-design-system';
import Sentry from '@/services/sentry/Sentry';
import {SCREEN_NAMES} from '@/lib/analytics/analyticsConstants';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import RecruitCrmActionsBar from '@/views/creators/recruit-crm/components/actions-bar';
import RecruitByEmailModal from '@/views/creators/recruit-crm/components/recruit-by-email-modal';
import RemoveConfirmationModal from '@/views/creators/recruit-crm/components/remove-confirmation-modal';
import RecruitCrmToolbar from '@/views/creators/recruit-crm/components/toolbar';
import {useRecruitCrmState} from '@/views/creators/recruit-crm/hooks/useRecruitCrmState';
import useRecruitmentSorting from '@/views/creators/recruit-crm/hooks/useRecruitmentSorting';
import useRecruitmentsTableColumns from '@/views/creators/recruit-crm/hooks/useRecruitmentsTableColumns';
import {
  RECRUITMENT_STATUS_PENDING_VETTING,
  RECRUITMENT_STATUS_VETTING_FAILED,
  type Recruitment,
  type RecruitmentSortBy,
} from '@/views/creators/recruit-crm/types';
import {
  computeBulkFlashStatus,
  countBulkFailures,
  computeBulkFlashMessage,
} from '@/views/creators/recruit-crm/utils/bulkActionFlash';
import FloatingPopperBar from '@/components/floating-popper-bar';
import MessageCreatorsModal, {
  MessageCreator,
} from '@/components/message-creators-modal';
import Page from '@/components/page';
import Table from '@/components/table';
import useTrackSearchButtonEvent from '@/hooks/analytics/use-track-search-button-event';
import useRecruitToCrm from '@/hooks/mutations/creators/use-recruit-to-crm';
import useRemoveRecruitment from '@/hooks/mutations/creators/use-remove-recruitment';
import useBulkRemoveRecruitments from '@/hooks/mutations/recruitments/use-bulk-remove-recruitments';
import useBulkRequestVetting from '@/hooks/mutations/recruitments/use-bulk-request-vetting';
import useRecruitmentBulkEmails from '@/hooks/mutations/recruitments/use-recruitment-bulk-emails';
import styles from './RecruitCrm.module.scss';

function RecruitCrm() {
  const trackSearchButtonEvent = useTrackSearchButtonEvent();
  const SCREEN_NAME = SCREEN_NAMES.RECRUIT_CRM;

  const onSearchButtonClick = useCallback(
    ({
      button_name,
      tab,
      creator_id,
    }: {
      button_name: string;
      tab?: string;
      creator_id?: string;
    }) => {
      trackSearchButtonEvent.pressed({
        button_name,
        tab,
        creator_id,
        screen_name: SCREEN_NAME,
        source: SCREEN_NAME,
      });
    },
    [trackSearchButtonEvent, SCREEN_NAME]
  );

  const {
    brandId,
    agencyId,
    recruitments,
    meta,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    filters,
    sorting,
    rowSelection,
    setFilters,
    setSorting,
    pageSelectionState,
    toggleRowSelection,
    selectAll,
    clearRowSelection,
    modals,
    openRemoveConfirmation,
    closeRemoveConfirmation,
    openBulkRemoveConfirmation,
    closeBulkRemoveConfirmation,
    openRecruitByEmailModal,
    closeRecruitByEmailModal,
    openBulkEmailModal,
    closeBulkEmailModal,
    openBulkRequestVetting,
    closeBulkRequestVetting,
  } = useRecruitCrmState();

  // Sorting handlers (encapsulates all sorting logic)
  const {tableSorting, handleToolbarSortChange, handleTableSortChange} =
    useRecruitmentSorting(sorting, setSorting);

  // Analytics wrapper for filter changes
  const handleFilterChange = useCallback(
    (newFilters: typeof filters) => {
      // Track status filter changes
      const statusFilter = newFilters.status;
      if (statusFilter) {
        const statusTab = Array.isArray(statusFilter)
          ? statusFilter.join(',')
          : statusFilter;
        onSearchButtonClick({
          button_name: 'status_filter',
          tab: String(statusTab),
        });
      }

      setFilters(newFilters);
    },
    [setFilters, onSearchButtonClick]
  );

  // Analytics wrapper for toolbar sort changes
  const handleToolbarSortChangeWithTracking = useCallback(
    (sortBy: RecruitmentSortBy) => {
      onSearchButtonClick({button_name: 'sort', tab: sortBy});
      handleToolbarSortChange(sortBy);
    },
    [handleToolbarSortChange, onSearchButtonClick]
  );

  // Analytics wrapper for table sort changes
  const handleTableSortChangeWithTracking = useCallback(
    (newSorting: MRT_SortingState) => {
      const columnId = newSorting?.[0]?.id || 'unknown';
      onSearchButtonClick({button_name: 'sort', tab: columnId});
      handleTableSortChange(newSorting);
    },
    [handleTableSortChange, onSearchButtonClick]
  );

  // Analytics wrapper for row selection
  const handleToggleRowSelection = useCallback(
    (id: string) => {
      const isSelected = rowSelection[id];
      const selected = !isSelected;
      onSearchButtonClick({
        button_name: selected ? 'select_creator' : 'deselect_creator',
        creator_id: id,
      });
      toggleRowSelection(id, selected);
    },
    [rowSelection, toggleRowSelection, onSearchButtonClick]
  );

  // Analytics wrapper for select all
  const handleSelectAll = useCallback(() => {
    onSearchButtonClick({button_name: 'select_all_page'});
    selectAll();
  }, [selectAll, onSearchButtonClick]);

  // Analytics wrapper for clear selection
  const handleClearSelection = useCallback(() => {
    onSearchButtonClick({button_name: 'clear_selection'});
    clearRowSelection();
  }, [clearRowSelection, onSearchButtonClick]);

  const removeRecruitmentMutation = useRemoveRecruitment();
  const bulkRemoveRecruitmentsMutation = useBulkRemoveRecruitments();
  const recruitToCrmMutation = useRecruitToCrm();
  const bulkEmailsMutation = useRecruitmentBulkEmails();
  const bulkRequestVettingMutation = useBulkRequestVetting();

  const handleStartVetting = useCallback(
    (recruitment: Recruitment) => {
      onSearchButtonClick({
        button_name: 'start_vetting',
        creator_id: recruitment.profileId || '',
      });

      if (!recruitment.id || !brandId || !agencyId) {
        raiseFlashMessageError({
          title: translate('Failed to start vetting'),
          subtitle: translate('User or brand information is missing'),
        });
        return;
      }
      bulkRequestVettingMutation.mutate(
        {agencyId, crmRecruitmentIds: [recruitment.id], brandId},
        {
          onSuccess: () => {
            raiseFlashMessage({
              status: 'success',
              mode: 'light',
              message: translate('Vetting started successfully'),
            });
          },
          onError: () => {
            raiseFlashMessageError({
              title: translate('Failed to start vetting'),
              subtitle: translate('Please try again or come back later'),
            });
          },
        }
      );
    },
    [agencyId, brandId, bulkRequestVettingMutation, onSearchButtonClick]
  );

  const handleRestoreToCrm = useCallback(
    async (recruitment: Recruitment) => {
      onSearchButtonClick({
        button_name: 'restore_to_crm',
        creator_id: recruitment.profileId || '',
      });

      if (!agencyId) {
        return;
      }
      const creatorIds = recruitment.profileId
        ? [recruitment.profileId]
        : undefined;
      const emails =
        !recruitment.profileId && recruitment.email
          ? [recruitment.email]
          : undefined;

      if (!creatorIds && !emails) {
        raiseFlashMessageError({
          title: translate('Failed to restore creator to CRM'),
          subtitle: translate('Please try again or come back later'),
        });
        return;
      }

      let isError = false;
      try {
        const response = await recruitToCrmMutation.mutateAsync({
          agencyId,
          creatorIds,
          emails,
        });
        const result = creatorIds
          ? response.results.profiles[0]
          : response.results.emails[0];
        if (!result?.success) {
          isError = true;
        }
      } catch {
        isError = true;
      }

      if (isError) {
        raiseFlashMessageError({
          title: translate('Failed to restore creator to CRM'),
          subtitle: translate('Please try again or come back later'),
        });
      } else {
        raiseFlashMessage({
          status: 'success',
          mode: 'light',
          message: translate('Creator restored to CRM'),
        });
      }
    },
    [agencyId, recruitToCrmMutation, onSearchButtonClick]
  );

  const actions = useMemo(
    () => ({
      onRemove: openRemoveConfirmation,
      onRestoreToCrm: handleRestoreToCrm,
      onStartVetting: handleStartVetting,
      isStartVettingLoading: bulkRequestVettingMutation.isLoading,
      onRowSelect: handleToggleRowSelection,
      rowSelection,
      pageSelectionState,
      onSelectAll: handleSelectAll,
      onClearSelection: handleClearSelection,
    }),
    [
      openRemoveConfirmation,
      handleRestoreToCrm,
      handleStartVetting,
      bulkRequestVettingMutation.isLoading,
      handleToggleRowSelection,
      rowSelection,
      pageSelectionState,
      handleSelectAll,
      handleClearSelection,
    ]
  );

  const messageCreators: MessageCreator[] = modals.bulkEmail
    ? recruitments
        .filter((recruitment) =>
          modals.bulkEmail!.creatorIds.includes(recruitment.id)
        )
        .map((recruitment) => ({
          id: recruitment.id,
          displayName: [recruitment.firstName, recruitment.lastName]
            .filter(Boolean)
            .join(' '),
          imageUrl: '',
        }))
    : [];

  const handleOpenMessageCreators = useCallback(() => {
    onSearchButtonClick({button_name: 'send_email'});
    openBulkEmailModal({creatorIds: Object.keys(rowSelection)});
  }, [openBulkEmailModal, rowSelection, onSearchButtonClick]);

  const handleMessageCreatorsSubmit = useCallback(
    async (subject: string, body: string) => {
      onSearchButtonClick({button_name: 'submit_bulk_email'});

      if (!agencyId) return;
      const ids = modals.bulkEmail?.creatorIds ?? [];
      if (ids.length === 0) return;
      try {
        await bulkEmailsMutation.mutateAsync({
          agencyId,
          crmRecruitmentIds: ids,
          subject,
          body,
        });
        handleClearSelection();
      } catch {
        raiseFlashMessageError({
          title: translate('Failed to send emails'),
          subtitle: translate('Please try again'),
        });
        throw new Error('Failed to send emails');
      }
    },
    [
      agencyId,
      modals.bulkEmail,
      bulkEmailsMutation,
      handleClearSelection,
      onSearchButtonClick,
    ]
  );

  const handleOpenBulkRemove = useCallback(() => {
    onSearchButtonClick({button_name: 'remove'});
    openBulkRemoveConfirmation(Object.keys(rowSelection));
  }, [openBulkRemoveConfirmation, rowSelection, onSearchButtonClick]);

  const handleBulkRemoveConfirm = useCallback(() => {
    onSearchButtonClick({button_name: 'confirm_bulk_remove'});

    const removeIds = modals.bulkRemoveConfirmation?.recruitmentIds;
    if (!removeIds?.length || !agencyId) {
      closeBulkRemoveConfirmation();
      return;
    }

    bulkRemoveRecruitmentsMutation.mutate(
      {agencyId, crmRecruitmentIds: removeIds},
      {
        onSuccess: (data) => {
          const successCount = data.success.length;
          const failedCount = countBulkFailures(data.failures);

          raiseFlashMessage({
            status: computeBulkFlashStatus(successCount, failedCount),
            mode: 'light',
            message: computeBulkFlashMessage(successCount, failedCount, {
              allSuccess: 'creators removed from CRM',
              partial: 'bulk remove partial',
              allFailed: 'failed to remove creators count',
            }),
          });

          closeBulkRemoveConfirmation();
          handleClearSelection();
        },
        onError: () => {
          raiseFlashMessageError({
            title: translate('Failed to remove creators from CRM'),
            subtitle: translate('Please try again or come back later'),
          });
        },
      }
    );
  }, [
    modals.bulkRemoveConfirmation,
    agencyId,
    bulkRemoveRecruitmentsMutation,
    closeBulkRemoveConfirmation,
    handleClearSelection,
    onSearchButtonClick,
  ]);

  const handleOpenBulkRequestVetting = useCallback(() => {
    onSearchButtonClick({button_name: 'request_vetting'});

    const selectedIds = Object.keys(rowSelection);
    const eligibleIds = recruitments
      .filter(
        (r) =>
          selectedIds.includes(r.id) &&
          [
            RECRUITMENT_STATUS_PENDING_VETTING,
            RECRUITMENT_STATUS_VETTING_FAILED,
          ].includes(r.status)
      )
      .map((r) => r.id);

    if (eligibleIds.length === 0) {
      raiseFlashMessageError({
        title: translate('No eligible creators'),
        subtitle: translate(
          'Only creators with pending vetting or vetting failed status can be vetted'
        ),
      });
      return;
    }

    openBulkRequestVetting(eligibleIds);
  }, [rowSelection, recruitments, openBulkRequestVetting, onSearchButtonClick]);

  const handleBulkRequestVettingConfirm = useCallback(() => {
    onSearchButtonClick({button_name: 'confirm_bulk_request_vetting'});

    const ids = modals.bulkRequestVetting?.recruitmentIds;
    if (!ids?.length || !agencyId || !brandId) {
      closeBulkRequestVetting();
      return;
    }

    bulkRequestVettingMutation.mutate(
      {agencyId, crmRecruitmentIds: ids, brandId},
      {
        onSuccess: (data) => {
          const successCount = data.success.length;
          const failedCount = countBulkFailures(data.failures);

          raiseFlashMessage({
            status: computeBulkFlashStatus(successCount, failedCount),
            mode: 'light',
            message: computeBulkFlashMessage(successCount, failedCount, {
              allSuccess: 'vetting requested for creators',
              partial: 'vetting requested partial',
              allFailed: 'failed to request vetting count',
            }),
          });

          closeBulkRequestVetting();
          handleClearSelection();
        },
        onError: () => {
          raiseFlashMessageError({
            title: translate('Failed to request vetting'),
            subtitle: translate('Please try again or come back later'),
          });
        },
      }
    );
  }, [
    modals.bulkRequestVetting,
    agencyId,
    brandId,
    bulkRequestVettingMutation,
    closeBulkRequestVetting,
    handleClearSelection,
    onSearchButtonClick,
  ]);

  // Confirm remove action
  const handleRemoveConfirm = useCallback(() => {
    const recruitmentId = modals.removeConfirmation?.recruitmentId;

    onSearchButtonClick({
      button_name: 'confirm_remove',
      creator_id: recruitmentId || '',
    });

    if (!recruitmentId || !agencyId) {
      Sentry.captureMessage(
        'Remove confirmation called with missing recruitmentId or agencyId',
        {
          extra: {
            recruitmentId: recruitmentId ?? null,
            agencyId: agencyId ?? null,
          },
        }
      );
      raiseFlashMessageError({
        title: translate('Failed to remove creator from CRM'),
        subtitle: translate('Please try again or come back later'),
      });
      closeRemoveConfirmation();
      return;
    }

    removeRecruitmentMutation.mutate(
      {recruitmentId, agencyId},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: translate('Creator removed from CRM'),
          });
          closeRemoveConfirmation();
        },
        onError: () => {
          raiseFlashMessageError({
            title: translate('Failed to remove creator from CRM'),
            subtitle: translate('Please try again or come back later'),
          });
        },
      }
    );
  }, [
    modals.removeConfirmation,
    agencyId,
    removeRecruitmentMutation,
    closeRemoveConfirmation,
    onSearchButtonClick,
  ]);

  // Table columns
  const columns = useRecruitmentsTableColumns(actions);

  const totalResults = meta?.totalCount || 0;
  const selectedIds = Object.keys(rowSelection);
  const hasSelection = selectedIds.length > 0;

  const hasEligibleForVetting = useMemo(() => {
    if (!hasSelection) return false;
    return recruitments.some(
      (r) =>
        selectedIds.includes(r.id) &&
        [
          RECRUITMENT_STATUS_PENDING_VETTING,
          RECRUITMENT_STATUS_VETTING_FAILED,
        ].includes(r.status)
    );
  }, [hasSelection, selectedIds, recruitments]);

  // Row click navigation
  const [, setSearchParams] = useSearchParams();
  const handleRowClick = useCallback(
    (row: Recruitment) => {
      onSearchButtonClick({
        button_name: 'open_creator_profile',
        creator_id: row.profileId || '',
      });

      if (!row.profileId) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('creatorProfileId', row.profileId as string);
        return next;
      });
    },
    [setSearchParams, onSearchButtonClick]
  );

  // Column visibility
  const visibleColumns = useMemo(
    () => createColumnVisibilityMap(columns),
    [columns]
  );

  // Loading state
  if (!agencyId) {
    return (
      <Page
        id="recruit-crm"
        className={styles.container}
        childrenClassName={styles.content}
      >
        <Headline size="lg">{translate('CRM Recruit Tracking')}</Headline>
        <div>{translate('Loading...')}</div>
      </Page>
    );
  }

  return (
    <Page
      id="recruit-crm"
      className={styles.container}
      childrenClassName={styles.content}
    >
      <Headline size="lg">{translate('CRM Recruit Tracking')}</Headline>

      <RecruitCrmToolbar
        totalResults={totalResults}
        filters={filters}
        sorting={sorting}
        onFilterChange={handleFilterChange}
        onSortChange={handleToolbarSortChangeWithTracking}
        onRecruitByEmail={() => {
          onSearchButtonClick({button_name: 'add_by_email'});
          openRecruitByEmailModal();
        }}
      />

      <FloatingPopperBar
        className={styles.floatingPopperBar}
        isOpen={hasSelection}
        placement="bottom"
        renderContent={() => (
          <RecruitCrmActionsBar
            selectedCount={selectedIds.length}
            onMessageCreators={handleOpenMessageCreators}
            onRequestVetting={handleOpenBulkRequestVetting}
            showRequestVetting={hasEligibleForVetting}
            onBulkRemove={handleOpenBulkRemove}
            onClearSelection={handleClearSelection}
          />
        )}
      >
        <Table
          data={recruitments}
          columns={columns}
          isLoadingInitial={isLoading}
          isLoading={isFetching}
          showProgressBars={isFetching}
          visibleColumns={visibleColumns}
          rowSelection={rowSelection}
          sorting={tableSorting}
          onSorting={handleTableSortChangeWithTracking}
          enableSorting
          manualSorting
          onRowClick={handleRowClick}
          isFirstRowBorder={false}
          classes={{
            paper: styles.tablePaper,
            container: styles.tableContainer,
            tableHeadRow: styles.tableHeadRow,
            footer: hasSelection ? styles.tableFooterWithBar : undefined,
          }}
          onEndReached={hasNextPage ? fetchNextPage : undefined}
        />
      </FloatingPopperBar>

      <RemoveConfirmationModal
        open={modals.removeConfirmation !== null}
        onClose={closeRemoveConfirmation}
        onConfirm={handleRemoveConfirm}
        isRemoving={removeRecruitmentMutation.isLoading}
      />

      <RemoveConfirmationModal
        open={modals.bulkRemoveConfirmation !== null}
        onClose={closeBulkRemoveConfirmation}
        onConfirm={handleBulkRemoveConfirm}
        isRemoving={bulkRemoveRecruitmentsMutation.isLoading}
        title={translate('remove confirmation title', {
          count: modals.bulkRemoveConfirmation?.recruitmentIds.length ?? 0,
        })}
        message={translate('bulk remove confirmation', {
          count: modals.bulkRemoveConfirmation?.recruitmentIds.length ?? 0,
        })}
        confirmLabel={translate('Remove')}
        confirmingLabel={translate('Removing...')}
      />

      <RemoveConfirmationModal
        open={modals.bulkRequestVetting !== null}
        onClose={closeBulkRequestVetting}
        onConfirm={handleBulkRequestVettingConfirm}
        isRemoving={bulkRequestVettingMutation.isLoading}
        title={translate('Request Vetting')}
        message={translate('bulk request vetting confirmation', {
          count: modals.bulkRequestVetting?.recruitmentIds.length ?? 0,
        })}
        confirmLabel={translate('Request Vetting')}
        confirmingLabel={translate('Requesting...')}
      />

      {agencyId && (
        <RecruitByEmailModal
          open={modals.recruitByEmail !== null}
          agencyId={agencyId}
          onClose={closeRecruitByEmailModal}
        />
      )}

      <MessageCreatorsModal
        open={modals.bulkEmail !== null}
        onClose={closeBulkEmailModal}
        creators={messageCreators}
        onSend={handleMessageCreatorsSubmit}
        onPlaceholderInsert={(placeholder) =>
          onSearchButtonClick({
            button_name: `insert_placeholder_${placeholder}`,
          })
        }
        onStepChange={(step) =>
          onSearchButtonClick({button_name: `message_creators_${step}`})
        }
      />
    </Page>
  );
}

/**
 * Creates visibility map for all table columns
 */
function createColumnVisibilityMap(
  columns: Array<{id?: string; accessorKey?: string}>
): Record<string, boolean> {
  return columns.reduce(
    (acc, col) => {
      const id = col.id || col.accessorKey;
      if (id) {
        acc[String(id)] = true;
      }
      return acc;
    },
    {} as Record<string, boolean>
  );
}

export default RecruitCrm;
